import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { AccountingPeriod, GenerateAccountingPeriodsRequest, PostingDateDeviation } from '../models/accounting-period/accounting-period';

@Injectable({ providedIn: 'root' })
export class AccountingPeriodProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/accounting-periods';

  /** All periods for a company + fiscal year (sorted by period). */
  public getByCompanyAndYear(companyReference: string, fiscalYear: number): Observable<ApiResponse<AccountingPeriod[]>> {
    return this.httpClient.get<ApiResponse<AccountingPeriod[]>>(`${this.baseUrl}/company/${companyReference}/year/${fiscalYear}`);
  }

  /** Idempotently generate the 12 monthly periods for a company + fiscal year. */
  public generateYear(request: GenerateAccountingPeriodsRequest): Observable<ApiResponse<AccountingPeriod[]>> {
    return this.httpClient.post<ApiResponse<AccountingPeriod[]>>(`${this.baseUrl}/generate`, request);
  }

  public open(id: string): Observable<ApiResponse<AccountingPeriod>> {
    return this.httpClient.patch<ApiResponse<AccountingPeriod>>(`${this.baseUrl}/${id}/open`, {});
  }

  public close(id: string): Observable<ApiResponse<AccountingPeriod>> {
    return this.httpClient.patch<ApiResponse<AccountingPeriod>>(`${this.baseUrl}/${id}/close`, {});
  }

  /** Close for normal postings but keep period-end / adjustment (closing) postings open. */
  public closingOnly(id: string): Observable<ApiResponse<AccountingPeriod>> {
    return this.httpClient.patch<ApiResponse<AccountingPeriod>>(`${this.baseUrl}/${id}/closing-only`, {});
  }

  /** Reopen a closed / closing-only period for normal postings. */
  public reopen(id: string): Observable<ApiResponse<AccountingPeriod>> {
    return this.httpClient.patch<ApiResponse<AccountingPeriod>>(`${this.baseUrl}/${id}/reopen`, {});
  }

  /** Non-blocking posting-date deviation check (#4714). `companyReference` may be the company id OR its site id. */
  public checkPostingDateDeviation(companyReference: string, postingDate: string): Observable<ApiResponse<PostingDateDeviation>> {
    const params = new HttpParams().set('companyReference', companyReference).set('postingDate', postingDate);
    return this.httpClient.get<ApiResponse<PostingDateDeviation>>(`${this.baseUrl}/posting-date-deviation`, { params });
  }
}
